import React, { useState, useRef, useEffect } from 'react';

/**
 * A reusable card component that fades in when it becomes visible in the viewport
 * and fades out when it scrolls out of view.
 * @param {object} props - The component props.
 * @param {string} props.imageSrc - The source URL for the image.
 * @param {string} props.altText - The alt text for the image.
 * @param {string} props.title - The title of the card.
 * @param {string} props.description - The description text for the card.
 */
const FadeInCard = ({ imageClass, imageSrc, altText, title, description, selected }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const cardRef = useRef(null);
  const maxLength = 120;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Set isVisible based on whether the entry is intersecting
          setIsVisible(entry.isIntersecting);
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the item is visible
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    // Cleanup function to unobserve when the component unmounts
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []); // Empty dependency array ensures this effect runs only once on mount

  const toggleExpansion = () => {
    setIsExpanded(!isExpanded);
  }

  const getDisplayDescription = () => {
    // Remove <br /> tags for accurate character count before truncation
    const plainTextDescription = description.replace(/<br\s*\/?>/gi, ' ');

    if (plainTextDescription.length > maxLength && !isExpanded) {
      // Find the last space within the maxLength to avoid cutting words
      const truncated = plainTextDescription.substring(0, maxLength);
      const lastSpaceIndex = truncated.lastIndexOf(' ');
      return (lastSpaceIndex !== -1 ? truncated.substring(0, lastSpaceIndex) : truncated) + '...';
    }
    return description; // Return original description with <br /> if expanded or short enough
  };

  return (
    <>
      <div id={title}
        ref={cardRef}
        className={`bg-card-background p-4 rounded-lg shadow-md transition-all duration-700 ease-out
          ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
          ${selected ? 'ring-4 ring-accent-main' : ''}`}
        style={{ scrollMargin: '120px' }}
      >
        <div className={`w-full h-48 flex items-center justify-center bg-gray-100 rounded-md mb-4 shadow-sm ${imageClass}`}>
          <img
            src={imageSrc}
            alt={altText}
            className={`max-w-full max-h-full object-contain cursor-pointer`}
            onClick={() => setShowModal(true)}
            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/400x300/CCCCCC/333333?text=Image+Unavailable"; }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>
        <h3 className="text-xl font-semibold text-text-dark mb-2">{title}</h3>
        <p className="text-text-muted text-sm">
          {getDisplayDescription().split('\n').map((line, index) => (
            <span key={index}>{line}<br /></span>
          ))}
        </p>
        {description.replace(/<br\s*\/?>/gi, ' ').length > maxLength && (
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevent card click from triggering when button is clicked
              toggleExpansion();
            }}
            className="text-accent-main hover:text-accent-hover text-xs font-semibold mt-2 focus:outline-none"
          >
            {isExpanded ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black bg-opacity-80 py-20 px-5" onClick={() => setShowModal(false)}>
          <img
            src={imageSrc}
            alt={altText}
            className="h-full max-h-screen max-w-screen w-auto rounded-lg shadow-2xl object-contain"
            onClick={e => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          />
          <button
            className="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-50 rounded-full px-3 py-1 hover:bg-opacity-80"
            onClick={() => setShowModal(false)}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
      )}
    </>
  );
};

export default FadeInCard;
